import { Card, EmptyState, Pagination, Skeleton } from "@heroui/react";
import { useEffect, useState } from "react";

import { useDebouncedValue } from "@/src/hooks/useDebouncedValue";
import { useTranslations } from "@/src/i18n/i18n";
import { EventFilters } from "@/src/pages/Events/components/EventFilters";
import { EventRow } from "@/src/pages/Events/components/EventRow";
import { useEventFilters } from "@/src/pages/Events/hooks/useEventFilters";
import { useEvents } from "@/src/pages/Events/hooks/useEvents";

/**
 * Liste des événements (`/events`).
 *
 * Vingt par page, cherchables par nom ou organisateur, filtrables par statut et
 * autour d'un point. Tout vient de `GET /moderation/events` — la seule route qui
 * énumère des événements pour l'administration, et la seule qui rende les
 * **trois** statuts (`close`, c'est-à-dire les parties jouées, compris) ainsi
 * que l'adresse non masquée.
 *
 * ⚠️ **Les filtres vivent dans l'URL** (`useEventFilters`), pas dans un
 * `useState` : on ouvre une fiche depuis cette liste, et le retour arrière doit
 * ramener la même page de la même recherche.
 */
export function Events() {
  const { filters, update } = useEventFilters();
  const t = useTranslations("events");

  // Le champ est piloté localement pour rester fluide sous la frappe ; l'URL
  // ne suit qu'après la pause, sinon chaque lettre écrirait une entrée
  // d'historique et une requête.
  const [term, setTerm] = useState(filters.q);
  const debounced = useDebouncedValue(term);

  useEffect(() => {
    if (debounced !== filters.q) update({ q: debounced });
  }, [debounced, filters.q, update]);

  const { events, total, pageCount, isLoading, isError } = useEvents(filters);

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col gap-5">
      <EventFilters
        filters={filters}
        term={term}
        onChange={update}
        onTermChange={setTerm}
      />

      {/* Le total ne s'affiche qu'une fois connu : « 0 événement » pendant le
          chargement dirait le contraire de la vérité. */}
      {isLoading || isError ? null : (
        <p className="text-sm text-muted">{t("total", { count: total })}</p>
      )}

      {isLoading ? (
        <div aria-hidden className="flex flex-col gap-2">
          {[0, 1, 2, 3, 4].map((row) => (
            <Skeleton className="h-16 w-full rounded-lg" key={row} />
          ))}
        </div>
      ) : null}

      {/* L'échec a son propre écran en plus du toast : sans cette branche, une
          panne se lit comme une base vide — le message le plus rassurant
          possible, et le plus faux. */}
      {isError ? (
        <Notice description={t("error-description")} title={t("error-title")} />
      ) : null}

      {!isLoading && !isError && events.length === 0 ? (
        <Notice
          description={
            filters.q || filters.status !== "all" || filters.lat !== null
              ? t("empty-filtered")
              : t("empty-none")
          }
          title={t("empty-title")}
        />
      ) : null}

      {events.length > 0 && !isLoading ? (
        <Card className="p-2">
          <ul className="flex flex-col">
            {events.map((event) => (
              <li key={event.id}>
                <EventRow event={event} />
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {/*
        Précédent / suivant plutôt qu'une rangée de numéros : le résumé dit où
        l'on est, ce que les numéros ne font qu'impliquer.
      */}
      {pageCount > 1 && !isLoading ? (
        <Pagination className="justify-between" size="sm">
          <Pagination.Previous
            isDisabled={filters.page <= 1}
            onPress={() => update({ page: filters.page - 1 })}
          >
            <Pagination.PreviousIcon />
            {t("previous")}
          </Pagination.Previous>

          <Pagination.Summary>
            {t("page-of", { page: filters.page, pages: pageCount })}
          </Pagination.Summary>

          <Pagination.Next
            isDisabled={filters.page >= pageCount}
            onPress={() => update({ page: filters.page + 1 })}
          >
            {t("next")}
            <Pagination.NextIcon />
          </Pagination.Next>
        </Pagination>
      ) : null}
    </div>
  );
}

function Notice({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <Card className="p-10">
      <Card.Content>
        <EmptyState className="flex flex-col items-center gap-2 text-center">
          <p className="text-base font-medium">{title}</p>
          <p className="max-w-md text-pretty text-muted">{description}</p>
        </EmptyState>
      </Card.Content>
    </Card>
  );
}
